import axios from 'axios';
import { BACKEND_URL } from './backendUrl';

const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('easystay_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const auth = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me', { headers: { Authorization: localStorage.getItem('easystay_token') } }),
};

export const hotels = {
  getPublic: (id) => api.get(`/hotels/${id}/public`),
  listPublic: () => api.get('/hotels/public/list'),
  list: (ownerId) => api.get(`/hotels?owner_id=${ownerId}`),
  create: (data) => api.post('/hotels', data),
  update: (id, data) => api.put(`/hotels/${id}`, data),
};

export const rooms = {
  list: (hotelId) => api.get(`/hotels/${hotelId}/rooms`),
  create: (hotelId, data) => api.post(`/hotels/${hotelId}/rooms`, data),
  update: (roomId, updates) => api.put(`/rooms/${roomId}`, updates),
  remove: (roomId) => api.delete(`/rooms/${roomId}`),
};

export const bookings = {
  create: (data) => api.post('/bookings', data),
  listForHotel: (hotelId) => api.get(`/hotels/${hotelId}/bookings`),
  getByEmail: (email) => api.get(`/bookings/guest?email=${email}`),
  update: (id, data) => api.patch(`/bookings/${id}`, data),
  confirmPhone: (id, data) => api.post(`/bookings/${id}/confirm-phone`, data),
  cancel: (id, data) => api.post(`/bookings/${id}/cancel`, data),
};

export const chat = {
  send: (data) => api.post('/chat', data),
};

export const pricing = {
  calculate: (checkIn, pricePerNight, nights, guestEmail = null, hotelId = null) => {
    let url = `/pricing?check_in=${checkIn}&price_per_night=${pricePerNight}&nights=${nights}`;
    if (guestEmail) url += `&guest_email=${encodeURIComponent(guestEmail)}`;
    if (hotelId) url += `&hotel_id=${hotelId}`;
    return api.get(url);
  },
};

export const admin = {
  stats: () => api.get('/admin/stats'),
  hotels: (status) => api.get(`/admin/hotels${status ? `?status=${status}` : ''}`),
  verifyHotel: (id, data) => api.patch(`/admin/hotels/${id}/verify`, data),
};

export const analytics = {
  get: (hotelId) => api.get(`/hotels/${hotelId}/analytics`),
  chat: (hotelId, data) => api.post(`/hotels/${hotelId}/analytics/chat`, data),
};

export const confirmations = {
  list: (hotelId, status) => api.get(`/hotels/${hotelId}/confirmations${status ? `?status=${status}` : ''}`),
};

export const marketplace = {
  list: (status) => api.get(`/marketplace/listings${status ? `?status=${status}` : ''}`),
  create: (data) => api.post('/marketplace/listings', data),
  buy: (id, data) => api.post(`/marketplace/listings/${id}/buy`, data),
  cancel: (id, data) => api.post(`/marketplace/listings/${id}/cancel`, data),
};

export const guests = {
  profile: (email) => api.get(`/guests/profile?email=${email}`),
  recommendations: (email) => api.get(`/guests/recommendations?email=${email}`),
};

export const notifications = {
  list: (hotelId, unreadOnly) => api.get(`/hotels/${hotelId}/notifications${unreadOnly ? '?unread_only=true' : ''}`),
  markRead: (id) => api.post(`/notifications/${id}/read`),
};

export const telegram = {
  setup: (hotelId, data) => api.post(`/hotels/${hotelId}/telegram/setup`, data),
  status: (hotelId) => api.get(`/hotels/${hotelId}/telegram/status`),
};

export const emails = {
  list: (hotelId) => api.get(`/hotels/${hotelId}/emails`),
};


export const mcp = {
  getStatus: (hotelId) => api.get(`/hotels/${hotelId}/mcp/status`),
  enable: (hotelId, enabled) => api.post(`/hotels/${hotelId}/mcp/enable`, { enabled }),
  rotateKey: (hotelId) => api.post(`/hotels/${hotelId}/mcp/rotate-key`),
};

export const aiTerms = {
  get: (hotelId) => api.get(`/hotels/${hotelId}/ai-terms`),
  update: (hotelId, terms) => api.put(`/hotels/${hotelId}/ai-terms`, terms),
};

export const payments = {
  getExchangeRates: () => api.get('/payments/exchange-rates').then(res => res.data),
  convertAmount: (usdAmount, chain) => api.post('/payments/convert', { usd_amount: usdAmount, chain }).then(res => res.data),
  verifyTransaction: (data) => api.post('/payments/verify-transaction', data).then(res => res.data),
  mockVerify: (bookingId, chain, amountUsd) => api.post('/payments/mock-verify', { booking_id: bookingId, chain, amount_usd: amountUsd }).then(res => res.data),
};

export const receipts = {
  generate: (bookingId) => api.post('/receipts/generate', { booking_id: bookingId }).then(res => res.data),
  getByBooking: (bookingId) => api.get(`/receipts/booking/${bookingId}`).then(res => res.data),
  getByHash: (hash) => api.get(`/receipts/${hash}`).then(res => res.data),
  verify: (hash) => api.post('/receipts/verify', { receipt_hash: hash }).then(res => res.data),
};

// Phase 8: Wallet management
hotels.getWalletAddresses = (hotelId) => api.get(`/hotels/${hotelId}/wallet-addresses`).then(res => res.data);
hotels.updateWalletAddresses = (hotelId, addresses) => api.put(`/hotels/${hotelId}/wallet-addresses`, addresses).then(res => res.data);

// Hotel management (hotel details + room CRUD)
hotels.updateDetails = (hotelId, details) => api.put(`/hotels/${hotelId}/details`, details).then(res => res.data);

// ID Verification System
export const verifications = {
  // Guest ID verification
  uploadGuestId: (data) => api.post('/verifications/guest-id/upload', data),
  getUserStatus: (email) => api.get(`/verifications/user/${email}/status`),
  revokeVerification: (email) => api.post(`/verifications/revoke/${email}`),
  
  // Hotel business verification
  uploadHotelDocs: (data) => api.post('/verifications/hotel-docs/upload', data),
  getHotelStatus: (hotelId) => api.get(`/verifications/hotel/${hotelId}/status`),
  
  // Admin management
  getPending: (type) => api.get(`/verifications/pending${type ? `?verification_type=${type}` : ''}`),
  approveHotel: (verificationId, data) => api.post(`/verifications/hotel/${verificationId}/approve`, data),
  
  // Privacy info
  getPrivacyInfo: () => api.get('/verifications/privacy-info'),
};

// Reliability Rewards System
export const reliability = {
  getGuestReliability: (email) => api.get(`/reliability/guest/${email}`),
  getQuote: (data) => api.post('/reliability/quote', data),
  getTiers: () => api.get('/reliability/tiers'),
  getHotelSettings: (hotelId) => api.get(`/reliability/hotel/${hotelId}/settings`),
  updateHotelSettings: (hotelId, settings) => api.post(`/reliability/hotel/${hotelId}/settings`, settings),
};

// Hotelogram - Guest Referral & Creator Economy
export const hotelogram = {
  getDashboard: (guestId) => api.get(`/hotelogram/dashboard/${guestId}`),
  getCreatorScore: (guestId) => api.get(`/hotelogram/score/guest/${guestId}`),
  getHotelScore: (hotelId) => api.get(`/hotelogram/score/hotel/${hotelId}`),
  getTiers: () => api.get('/hotelogram/tiers'),
  generateReferral: (data) => api.post('/hotelogram/referral/generate', data),
  getGuestReferrals: (guestId) => api.get(`/hotelogram/referral/guest/${guestId}`),
  trackClick: (code) => api.post('/hotelogram/referral/click', { referral_code: code }),
  createContent: (data) => api.post('/hotelogram/content/create', data),
  getHotelContent: (hotelId) => api.get(`/hotelogram/content/hotel/${hotelId}`),
  voteHelpful: (contentId) => api.post(`/hotelogram/content/${contentId}/helpful`),
  generateShareCard: (data) => api.post('/hotelogram/share-card/generate', data),
  getShareCards: (guestEmail) => api.get(`/hotelogram/share-cards/${guestEmail}`),
};

// Flash Bounty System
export const flashBounties = {
  getBoard: () => api.get('/flash-bounties/board'),
  getHotelBounties: (hotelId) => api.get(`/flash-bounties/hotel/${hotelId}`),
  getActiveBounties: (hotelId) => api.get(`/flash-bounties/hotel/${hotelId}/active`),
  createBounty: (data) => api.post('/flash-bounties/create', data),
  deactivateBounty: (bountyId, data) => api.post(`/flash-bounties/${bountyId}/deactivate`, data),
  getBountyDetail: (bountyId) => api.get(`/flash-bounties/${bountyId}`),
};

// NFT Lifecycle
export const nftLifecycle = {
  getStatus: (bookingId) => api.get(`/nft-lifecycle/status/${bookingId}`),
  getPortfolio: (email) => api.get(`/nft-lifecycle/portfolio/${email}`),
  getResaleListings: (hotelId) => api.get(`/nft-lifecycle/resale/listings${hotelId ? `?hotel_id=${hotelId}` : ''}`),
};

export const seed = () => api.post('/seed');

export default api;
